// Payroll - Senior Developer Enterprise Payroll Groups Management Component
import React, { useState, useEffect } from 'react';
import { hasAnyRole } from '../../utils/roleHelper';

const GROUP_TYPES = [
  { value: 'department', label: '📁 Department (Auto-Assigned)', icon: '📁', color: '#0284c7', hint: 'Rule-Based Group: Auto-assigns personnel belonging to the selected department.' },
  { value: 'force', label: '🌐 Force-Wide (Auto-Assigned)', icon: '🌐', color: '#1d4ed8', hint: 'Rule-Based Group: Auto-assigns all personnel across the Security Force.' },
  { value: 'unit', label: '🪖 Unit (Auto-Assigned)', icon: '🪖', color: '#7c3aed', hint: 'Rule-Based Group: Auto-assigns personnel belonging to the specified unit.' },
  { value: 'organization', label: '🏢 Organization (Auto-Assigned)', icon: '🏢', color: '#059669', hint: 'Rule-Based Group: Auto-assigns personnel belonging to the matching organization.' },
  { value: 'custom', label: '⚙️ Custom Category (Manual Only)', icon: '⚙️', color: '#92400e', hint: 'Custom Group: Strictly manual personnel assignment for special task forces or custom lists.' },
];

const PayrollGroupsTab = ({ api, user, armyTypeId, armyTypeName }) => {
  const [groups, setGroups] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Modal State
  const [activeManageGroup, setActiveManageGroup] = useState(null);

  // Form State
  const [form, setForm] = useState({ name: '', group_type: 'department', description: '', department_id: '' });
  const [editingGroup, setEditingGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });

  const canManage = hasAnyRole(user, ['admin', 'super_admin', 'finance_director', 'payroll_officer', 'finance_officer', 'finance']);

  useEffect(() => {
    setActiveManageGroup(null);
    setForm({ name: '', group_type: 'department', description: '', department_id: '' });
    setEditingGroup(null);
    fetchAll();
  }, [armyTypeId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [gRes, pRes, dRes] = await Promise.all([
        api.get(`/payroll_groups?army_type_id=${armyTypeId}`),
        api.get(`/army_personnel?army_type_id=${armyTypeId}`),
        api.get(`/departments?army_type_id=${armyTypeId}`)
      ]);

      const loadedGroups = gRes?.payroll_groups || gRes?.data?.payroll_groups || [];
      const loadedPersonnel = pRes?.army_personnel || pRes?.data?.army_personnel || [];
      const loadedDepts = Array.isArray(dRes) ? dRes : (dRes?.departments || dRes?.data || []);

      setGroups(loadedGroups);
      setPersonnel(loadedPersonnel);
      setDepartments(loadedDepts);

      // If a modal is open, keep its group reference up to date
      if (activeManageGroup) {
        const updatedG = loadedGroups.find(g => String(g.id) === String(activeManageGroup.id));
        if (updatedG) setActiveManageGroup(updatedG);
      }
    } catch (e) {
      console.error('Failed to fetch payroll groups data:', e);
      showToast('Error loading payroll groups data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (text, type = 'info') => {
    setMsg({ text, type });
    if (type === 'success') {
      setTimeout(() => setMsg({ text: '', type: '' }), 6000);
    }
  };

  const getPersonnelName = (p) => {
    if (!p) return 'Unknown';
    if (p.full_name) return p.full_name;
    if (p.first_name || p.last_name) return `${p.first_name || ''} ${p.last_name || ''}`.trim();
    if (p.name) return p.name;
    return `Personnel #${p.id}`;
  };

  // Group Form Handlers
  const handleSaveGroup = async (e) => {
    e.preventDefault();
    if (form.group_type === 'department' && !form.department_id) {
      return showToast('Please select a Linked Department for Department Group Type', 'error');
    }

    setLoading(true); setMsg({ text: '', type: '' });
    try {
      const payload = { payroll_group: { ...form, army_type_id: armyTypeId } };
      let res;
      if (editingGroup) {
        res = await api.put(`/payroll_groups/${editingGroup.id}`, payload);
      } else {
        res = await api.post('/payroll_groups', payload);
      }

      if (res?.response && !res.response.ok) {
        showToast(res.data?.error || res.data?.errors?.join(', ') || 'Error saving group', 'error');
      } else {
        showToast(
          editingGroup ? '✅ Group updated successfully!' : `✅ Group "${form.name}" created! Use ⚡ Auto-Populate to assign personnel in 1 click.`,
          'success'
        );
        setForm({ name: '', group_type: 'department', description: '', department_id: '' });
        setEditingGroup(null);
        fetchAll();
      }
    } catch (e) {
      showToast('Error saving payroll group', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (g) => {
    if (!confirm(`Delete payroll group "${g.name}"?\n\nAll members in this group will be unassigned.`)) return;
    setLoading(true);
    try {
      const res = await api.delete(`/payroll_groups/${g.id}`);
      if (res?.response && !res.response.ok) {
        showToast(res.data?.error || 'Cannot delete group', 'error');
      } else {
        showToast(`✅ Payroll group "${g.name}" deleted.`, 'success');
        if (activeManageGroup?.id === g.id) setActiveManageGroup(null);
        fetchAll();
      }
    } catch (e) {
      showToast('Cannot delete group', 'error');
    } finally {
      setLoading(false);
    }
  };

  // 1-Click Auto Populate Handler
  const handleAutoPopulate = async (g) => {
    let confirmMsg = `Auto-populate personnel into "${g.name}"?`;
    if (g.group_type === 'department') {
      const deptName = g.department?.name || 'the linked department';
      confirmMsg = `⚡ Auto-Populate Group "${g.name}"?\n\nThis will instantly assign all personnel belonging to department "${deptName}" into this group.`;
    } else if (g.group_type === 'force') {
      confirmMsg = `⚡ Auto-Populate Group "${g.name}"?\n\nThis will assign all unassigned personnel in "${armyTypeName}" into this group.`;
    }

    if (!confirm(confirmMsg)) return;

    setLoading(true); setMsg({ text: '', type: '' });
    try {
      const res = await api.post(`/payroll_groups/${g.id}/auto_populate`);
      if (res?.response && !res.response.ok) {
        showToast(res.data?.error || 'Failed to auto-populate group', 'error');
      } else {
        const count = res?.assigned_count || res?.data?.assigned_count || 0;
        showToast(`⚡ ${res?.message || `Successfully auto-populated ${count} personnel into "${g.name}"`}`, 'success');
        fetchAll();
      }
    } catch (e) {
      showToast('Error auto-populating personnel', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Quick Personnel Assign/Remove API calls
  const handleAssignIds = async (groupId, pIds) => {
    if (!pIds || pIds.length === 0) return;
    try {
      const res = await api.post(`/payroll_groups/${groupId}/add_personnel`, { personnel_ids: pIds });
      if (res?.response && !res.response.ok) {
        showToast(res.data?.error || 'Error assigning personnel', 'error');
      } else {
        showToast(`✅ Assigned ${pIds.length} personnel to group`, 'success');
        fetchAll();
      }
    } catch (e) {
      showToast('Error assigning personnel', 'error');
    }
  };

  const handleRemoveIds = async (groupId, pIds) => {
    if (!pIds || pIds.length === 0) return;
    try {
      const res = await api.post(`/payroll_groups/${groupId}/remove_personnel`, { personnel_ids: pIds });
      if (res?.response && !res.response.ok) {
        showToast(res.data?.error || 'Error removing personnel', 'error');
      } else {
        showToast(`Removed ${pIds.length} personnel from group`, 'info');
        fetchAll();
      }
    } catch (e) {
      showToast('Error removing personnel', 'error');
    }
  };

  // Metrics
  const forcePersonnel = personnel.filter(p => String(p.army_type_id) === String(armyTypeId));
  const assignedPersonnelCount = forcePersonnel.filter(p => p.payroll_group_id && String(p.payroll_group_id) !== 'null').length;
  const unassignedPersonnelCount = forcePersonnel.length - assignedPersonnelCount;
  const activeTypeObj = GROUP_TYPES.find(t => t.value === form.group_type);

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif', color: '#1e293b' }}>

      {/* 1. Header Banner & Metrics Bar */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        borderRadius: 12, padding: '20px 24px', marginBottom: 24, color: '#fff',
        boxShadow: '0 4px 12px rgba(15,23,42,0.15)', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontSize: 22 }}>📋</span>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, letterSpacing: '-0.02em', color: '#f8fafc' }}>
              Payroll Groups Management
            </h2>
            <span style={{ padding: '3px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.15)', fontSize: 12, fontWeight: 700, color: '#93c5fd' }}>
              {armyTypeName}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#94a3b8', maxWidth: 620 }}>
            Organize personnel into payroll groups by Department, Unit, Force, or Custom Categories. Allowances and deductions apply to group members with individual override hierarchy.
          </p>
        </div>

        {/* Quick Stats Badges */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ background: 'rgba(255,255,255,0.08)', padding: '10px 16px', borderRadius: 8, textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#38bdf8' }}>{groups.length}</div>
            <div style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 600 }}>Active Groups</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.08)', padding: '10px 16px', borderRadius: 8, textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#4ade80' }}>{assignedPersonnelCount}</div>
            <div style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 600 }}>Assigned Members</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.08)', padding: '10px 16px', borderRadius: 8, textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fbbf24' }}>{unassignedPersonnelCount}</div>
            <div style={{ fontSize: 11, color: '#cbd5e1', fontWeight: 600 }}>Unassigned Personnel</div>
          </div>
          <button
            onClick={fetchAll}
            title="Refresh Data"
            style={{
              padding: '10px 14px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, transition: 'all 0.2s'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Toast Alert */}
      {msg.text && (
        <div style={{
          marginBottom: 20, padding: '12px 18px', borderRadius: 8, fontSize: 13, fontWeight: 700,
          background: msg.type === 'error' ? '#fef2f2' : msg.type === 'warning' ? '#fffbeb' : '#f0fdf4',
          border: `1px solid ${msg.type === 'error' ? '#fca5a5' : msg.type === 'warning' ? '#fde68a' : '#86efac'}`,
          color: msg.type === 'error' ? '#991b1b' : msg.type === 'warning' ? '#92400e' : '#166534',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span>{msg.text}</span>
          <button onClick={() => setMsg({ text: '', type: '' })} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 800, color: 'inherit' }}>✕</button>
        </div>
      )}

      {/* 2. Group Creation / Editing Card */}
      {canManage && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 22, marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
              {editingGroup ? '✏️ Edit Payroll Group' : '➕ Create New Payroll Group'}
            </h3>
            {editingGroup && (
              <button
                type="button"
                onClick={() => {
                  setEditingGroup(null);
                  setForm({ name: '', group_type: 'department', description: '', department_id: '' });
                }}
                style={{ padding: '5px 12px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
              >
                Cancel Edit
              </button>
            )}
          </div>

          <form onSubmit={handleSaveGroup}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 14 }}>

              {/* Group Name */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#475569' }}>Group Name *</label>
                <input
                  required
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Logistics Department Group"
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                />
              </div>

              {/* Group Type */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#475569' }}>Group Type *</label>
                <select
                  value={form.group_type}
                  onChange={e => setForm({ ...form, group_type: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff', fontWeight: 600, boxSizing: 'border-box' }}
                >
                  {GROUP_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>

              {/* Dynamic Target Field Based on Group Type */}
              {form.group_type === 'department' && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#475569' }}>
                    Linked Department *
                  </label>
                  <select
                    value={form.department_id}
                    onChange={e => {
                      const deptId = e.target.value;
                      const deptObj = departments.find(d => String(d.id) === String(deptId));
                      setForm({
                        ...form,
                        department_id: deptId,
                        name: form.name || (deptObj ? `${deptObj.name} Group` : '')
                      });
                    }}
                    required
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box' }}
                  >
                    <option value="">Select Department...</option>
                    {departments
                      .filter(d => !d.army_type_id || String(d.army_type_id) === String(armyTypeId))
                      .map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              )}

              {form.group_type === 'unit' && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#475569' }}>
                    Linked Unit *
                  </label>
                  <select
                    value={form.department_id}
                    onChange={e => {
                      const deptId = e.target.value;
                      const deptObj = departments.find(d => String(d.id) === String(deptId));
                      setForm({
                        ...form,
                        department_id: deptId,
                        name: form.name || (deptObj ? `${deptObj.name} Unit` : '')
                      });
                    }}
                    required
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box' }}
                  >
                    <option value="">Select Unit...</option>
                    {departments
                      .filter(d => !d.army_type_id || String(d.army_type_id) === String(armyTypeId))
                      .map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
              )}

              {form.group_type === 'organization' && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#475569' }}>
                    Organization / Division Name *
                  </label>
                  <input
                    required
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value, name: form.name || (e.target.value ? `${e.target.value} Group` : '') })}
                    placeholder="e.g. Special Forces, Police HQ"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
              )}

              {form.group_type === 'force' && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#475569' }}>Scope</label>
                  <div style={{ padding: '9px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#1e40af' }}>
                    🌐 Entire Force: {armyTypeName}
                  </div>
                </div>
              )}

              {form.group_type === 'custom' && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#475569' }}>Scope</label>
                  <div style={{ padding: '9px 12px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, fontSize: 12, fontWeight: 700, color: '#92400e' }}>
                    ⚙️ Custom Group: Manual Assignment
                  </div>
                </div>
              )}

              {/* Description (for non-organization types) */}
              {form.group_type !== 'organization' && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, marginBottom: 4, color: '#475569' }}>Description</label>
                  <input
                    value={form.description}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    placeholder="Optional description / notes"
                    style={{ width: '100%', padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                  />
                </div>
              )}
            </div>

            {/* Smart Hint Bar */}
            {activeTypeObj && (
              <div style={{ marginBottom: 16, padding: '8px 14px', background: '#f8fafc', borderLeft: `4px solid ${activeTypeObj.color}`, borderRadius: 4, fontSize: 12, color: '#334155' }}>
                💡 <strong>Logic Hint:</strong> {activeTypeObj.hint}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="submit"
                disabled={loading}
                style={{
                  padding: '10px 24px', background: '#1b4332', color: '#fff', border: 'none', borderRadius: 6,
                  fontWeight: 700, fontSize: 13, cursor: loading ? 'wait' : 'pointer', boxShadow: '0 2px 4px rgba(27,67,50,0.2)'
                }}
              >
                {loading ? 'Saving...' : editingGroup ? '💾 Save Changes' : '➕ Create Group'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* 3. Payroll Groups Cards Grid */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
            Active Payroll Groups ({groups.length}) — {armyTypeName}
          </h3>
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
            Click <strong>👥 Manage Members</strong> to add/remove personnel
          </span>
        </div>

        {groups.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#475569' }}>No Payroll Groups Created</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>Create your first payroll group above to segment personnel for automated salary processing.</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16, padding: 20, background: '#f8fafc' }}>
            {groups.map((g) => {
              const typeMeta = GROUP_TYPES.find(t => t.value === g.group_type) || GROUP_TYPES[0];
              const groupMembers = forcePersonnel.filter(p => p.payroll_group_id && String(p.payroll_group_id) === String(g.id));
              const memberCount = g.personnel_count || groupMembers.length;

              return (
                <div
                  key={g.id}
                  style={{
                    background: '#fff', border: '1px solid #cbd5e1', borderRadius: 10, padding: 18,
                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)', transition: 'transform 0.15s, box-shadow 0.15s'
                  }}
                >
                  <div>
                    {/* Top Row: Title & Badge */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
                      <div>
                        <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>{g.name}</h4>
                        {g.description && <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>{g.description}</div>}
                      </div>

                      <span style={{
                        padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 800,
                        background: `${typeMeta.color}15`, color: typeMeta.color, border: `1px solid ${typeMeta.color}30`, whiteSpace: 'nowrap'
                      }}>
                        {typeMeta.icon} {typeMeta.label}
                      </span>
                    </div>

                    {/* Department Tag & Member Count */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 16 }}>
                      {g.department && (
                        <span style={{ padding: '3px 8px', borderRadius: 6, background: '#f1f5f9', border: '1px solid #cbd5e1', fontSize: 11, color: '#334155', fontWeight: 600 }}>
                          📁 Dept: <strong>{g.department.name}</strong>
                        </span>
                      )}

                      <span style={{ padding: '3px 10px', borderRadius: 12, background: memberCount > 0 ? '#dcfce7' : '#f1f5f9', color: memberCount > 0 ? '#15803d' : '#64748b', fontSize: 12, fontWeight: 800 }}>
                        👥 {memberCount} Active Members
                      </span>
                    </div>
                  </div>

                  {/* Actions Footer */}
                  <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {canManage && g.group_type !== 'custom' && (
                        <button
                          onClick={() => handleAutoPopulate(g)}
                          disabled={loading}
                          title="Auto-assign personnel to this group based on rules"
                          style={{
                            padding: '6px 12px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 6,
                            cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4
                          }}
                        >
                          ⚡ Auto-Populate
                        </button>
                      )}

                      <button
                        onClick={() => setActiveManageGroup(g)}
                        style={{
                          padding: '6px 14px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6,
                          cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4
                        }}
                      >
                        👥 Manage Members
                      </button>
                    </div>

                    {canManage && (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          onClick={() => {
                            setEditingGroup(g);
                            setForm({ name: g.name, group_type: g.group_type || 'department', description: g.description || '', department_id: g.department_id || '' });
                            window.scrollTo({ top: 180, behavior: 'smooth' });
                          }}
                          title="Edit Group Details"
                          style={{ padding: '5px 10px', background: '#f1f5f9', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteGroup(g)}
                          title="Delete Group"
                          style={{ padding: '5px 10px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 4. Enterprise Senior Developer Member Management Modal */}
      {activeManageGroup && (
        <MemberManagementModal
          group={activeManageGroup}
          allPersonnel={forcePersonnel}
          onClose={() => setActiveManageGroup(null)}
          onAssign={handleAssignIds}
          onRemove={handleRemoveIds}
          onAutoPopulate={handleAutoPopulate}
          getPersonnelName={getPersonnelName}
        />
      )}

    </div>
  );
};

// Sub-Component: Reusable Pagination Controls
const PaginationControls = ({ currentPage, totalItems, pageSize, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  if (totalPages <= 1) return null;

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid #e2e8f0', fontSize: 12 }}>
      <div style={{ color: '#64748b', fontWeight: 600 }}>
        Showing <strong>{((currentPage - 1) * pageSize) + 1}–{Math.min(currentPage * pageSize, totalItems)}</strong> of <strong>{totalItems}</strong> items
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button
          type="button"
          disabled={currentPage === 1}
          onClick={() => onPageChange(currentPage - 1)}
          style={{
            padding: '5px 12px', borderRadius: 6, border: '1px solid #cbd5e1',
            background: currentPage === 1 ? '#f1f5f9' : '#fff', color: currentPage === 1 ? '#94a3b8' : '#334155',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontWeight: 700
          }}
        >
          ◀ Prev
        </button>
        <span style={{ fontWeight: 700, color: '#334155', padding: '0 6px' }}>
          Page {currentPage} of {totalPages}
        </span>
        <button
          type="button"
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          style={{
            padding: '5px 12px', borderRadius: 6, border: '1px solid #cbd5e1',
            background: currentPage === totalPages ? '#f1f5f9' : '#fff', color: currentPage === totalPages ? '#94a3b8' : '#334155',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontWeight: 700
          }}
        >
          Next ▶
        </button>
      </div>
    </div>
  );
};

// Sub-Component: Senior Developer Enterprise Modal for Member Management
const MemberManagementModal = ({ group, allPersonnel, onClose, onAssign, onRemove, onAutoPopulate, getPersonnelName }) => {
  const [activeTab, setActiveTab] = useState('assigned'); // 'assigned' or 'add'
  const [filterMode, setFilterMode] = useState(group.department_id ? 'dept_only' : 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAddIds, setSelectedAddIds] = useState([]);
  const [selectedRemoveIds, setSelectedRemoveIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pagination State
  const [assignedPage, setAssignedPage] = useState(1);
  const [addPage, setAddPage] = useState(1);
  const PAGE_SIZE = 10;

  // Reset pagination on filter or tab change
  useEffect(() => {
    setAssignedPage(1);
    setAddPage(1);
  }, [searchQuery, filterMode, activeTab]);

  // Group members vs Unassigned personnel
  const assignedMembers = allPersonnel.filter(p => p.payroll_group_id && String(p.payroll_group_id) === String(group.id));
  const unassignedPersonnel = allPersonnel.filter(p => !p.payroll_group_id || String(p.payroll_group_id) === 'null');

  // Department matching unassigned
  const deptMatchingUnassigned = unassignedPersonnel.filter(p => group.department_id && String(p.department_id) === String(group.department_id));
  const addPool = (filterMode === 'dept_only' && group.department_id) ? deptMatchingUnassigned : unassignedPersonnel;

  // Filtered lists (search across Army ID, Name, Phone, Rank, Department, Subdepartment, Organization)
  const filterPersonnel = (p) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase().trim();
    const name = getPersonnelName(p).toLowerCase();
    const armyId = (p.army_id || '').toLowerCase();
    const rank = (p.rank || '').toLowerCase();
    const phone = (p.phone || p.phone_number || '').toLowerCase();
    const dept = (p.department?.name || p.department_name || '').toLowerCase();
    const subDept = (p.sub_department?.name || p.sub_department_name || '').toLowerCase();
    const org = (p.organization || p.army_type?.name || '').toLowerCase();

    return name.includes(q) ||
      armyId.includes(q) ||
      rank.includes(q) ||
      phone.includes(q) ||
      dept.includes(q) ||
      subDept.includes(q) ||
      org.includes(q);
  };

  const filteredAssigned = assignedMembers.filter(filterPersonnel);
  const filteredAddPool = addPool.filter(filterPersonnel);

  // Paginated slices
  const paginatedAssigned = filteredAssigned.slice((assignedPage - 1) * PAGE_SIZE, assignedPage * PAGE_SIZE);
  const paginatedAddPool = filteredAddPool.slice((addPage - 1) * PAGE_SIZE, addPage * PAGE_SIZE);

  const handleQuickAdd = async (personnelId) => {
    setIsSubmitting(true);
    await onAssign(group.id, [personnelId]);
    setIsSubmitting(false);
  };

  const handleBulkAdd = async () => {
    if (selectedAddIds.length === 0) return;
    setIsSubmitting(true);
    await onAssign(group.id, selectedAddIds);
    setSelectedAddIds([]);
    setIsSubmitting(false);
  };

  const handleQuickRemove = async (personnelId) => {
    setIsSubmitting(true);
    await onRemove(group.id, [personnelId]);
    setIsSubmitting(false);
  };

  const handleBulkRemove = async () => {
    if (selectedRemoveIds.length === 0) return;
    setIsSubmitting(true);
    await onRemove(group.id, selectedRemoveIds);
    setSelectedRemoveIds([]);
    setIsSubmitting(false);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20
    }}>
      <div style={{
        background: '#fff', borderRadius: 14, width: '100%', maxWidth: 940, maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)', overflow: 'hidden'
      }}>

        {/* Modal Header */}
        <div style={{
          padding: '18px 24px', background: '#0f172a', color: '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #1e293b'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#f8fafc' }}>
                👥 Manage Members: {group.name}
              </h3>
              <span style={{ padding: '2px 8px', borderRadius: 10, background: '#1e293b', color: '#38bdf8', fontSize: 11, fontWeight: 700, border: '1px solid #334155' }}>
                {group.group_type}
              </span>
            </div>
            {group.department && (
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
                Linked Department: <strong>{group.department.name}</strong>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {group.group_type !== 'custom' && (
              <button
                onClick={() => onAutoPopulate(group)}
                style={{
                  padding: '6px 14px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 6,
                  fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
                }}
              >
                ⚡ Resync Auto-Population
              </button>
            )}

            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Modal Sub-Header Tabs & Search */}
        <div style={{ padding: '12px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>

          {/* Main Tabs */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              onClick={() => { setActiveTab('assigned'); setSearchQuery(''); }}
              style={{
                padding: '7px 16px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                background: activeTab === 'assigned' ? '#1b4332' : '#e2e8f0',
                color: activeTab === 'assigned' ? '#fff' : '#475569'
              }}
            >
              Assigned Members ({assignedMembers.length})
            </button>

            {group.group_type === 'custom' ? (
              <button
                onClick={() => { setActiveTab('add'); setSearchQuery(''); }}
                style={{
                  padding: '7px 16px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 800, cursor: 'pointer',
                  background: activeTab === 'add' ? '#1d4ed8' : '#e2e8f0',
                  color: activeTab === 'add' ? '#fff' : '#475569'
                }}
              >
                ➕ Add Personnel ({unassignedPersonnel.length} Available)
              </button>
            ) : (
              <span style={{ fontSize: 12, color: '#0369a1', background: '#e0f2fe', padding: '6px 12px', borderRadius: 6, fontWeight: 700, border: '1px solid #bae6fd' }}>
                ⚡ Auto-Assigned Group ({group.group_type.toUpperCase()})
              </span>
            )}
          </div>

          {/* Search Box */}
          <div style={{ flex: '1 1 240px', maxWidth: 320 }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="🔍 Search ID, Name, Phone, Rank, Dept, Subdept, Org..."
              style={{ width: '100%', padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, background: '#fff', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        {/* Modal Content Body */}
        <div style={{ padding: 24, overflowY: 'auto', flex: 1, background: '#fff' }}>

          {/* TAB 1: ASSIGNED MEMBERS */}
          {activeTab === 'assigned' && (
            <div>
              {/* Bulk Actions Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>
                  Showing {filteredAssigned.length} of {assignedMembers.length} assigned members
                </div>

                {group.group_type === 'custom' && selectedRemoveIds.length > 0 && (
                  <button
                    onClick={handleBulkRemove}
                    disabled={isSubmitting}
                    style={{ padding: '6px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >
                    ❌ Remove Selected ({selectedRemoveIds.length})
                  </button>
                )}
              </div>

              {assignedMembers.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', border: '2px dashed #e2e8f0', borderRadius: 10 }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>👥</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#475569' }}>No Personnel Assigned</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>
                    {group.group_type === 'custom'
                      ? 'Click "➕ Add Personnel" tab above to manually assign personnel.'
                      : 'Click "⚡ Resync Auto-Population" above to automatically pull matching personnel into this group.'}
                  </div>
                </div>
              ) : filteredAssigned.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                  No assigned members matching <strong>"{searchQuery}"</strong>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                    {paginatedAssigned.map((p) => {
                      const isSelected = selectedRemoveIds.includes(p.id);
                      return (
                        <div
                          key={p.id}
                          style={{
                            padding: '12px 14px', border: `1px solid ${isSelected ? '#fca5a5' : '#e2e8f0'}`, borderRadius: 8,
                            background: isSelected ? '#fff5f5' : '#fafafa', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            {group.group_type === 'custom' && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  if (isSelected) setSelectedRemoveIds(selectedRemoveIds.filter(id => id !== p.id));
                                  else setSelectedRemoveIds([...selectedRemoveIds, p.id]);
                                }}
                              />
                            )}
                            <div>
                              <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a' }}>
                                {getPersonnelName(p)}
                              </div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>
                                ID: <strong style={{ color: '#0284c7' }}>{p.army_id || 'N/A'}</strong> | Rank: {p.rank || 'N/A'}
                              </div>
                            </div>
                          </div>

                          {group.group_type === 'custom' ? (
                            <button
                              onClick={() => handleQuickRemove(p.id)}
                              disabled={isSubmitting}
                              title="Remove from group"
                              style={{ padding: '4px 8px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                            >
                              Remove
                            </button>
                          ) : (
                            <span style={{ padding: '3px 8px', background: '#f1f5f9', color: '#475569', borderRadius: 5, fontSize: 11, fontWeight: 700 }}>
                              Auto-Assigned
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Assigned Tab Pagination Controls */}
                  <PaginationControls
                    currentPage={assignedPage}
                    totalItems={filteredAssigned.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setAssignedPage}
                  />
                </>
              )}
            </div>
          )}

          {/* TAB 2: ADD UNASSIGNED PERSONNEL */}
          {activeTab === 'add' && (
            <div>
              {/* Department Filter Toggle & Select All */}
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 14 }}>

                {/* Scope Filters */}
                {group.department_id ? (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      type="button"
                      onClick={() => setFilterMode('dept_only')}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: '1px solid #0284c7', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        background: filterMode === 'dept_only' ? '#0284c7' : '#f0f9ff',
                        color: filterMode === 'dept_only' ? '#fff' : '#0369a1'
                      }}
                    >
                      🎯 Linked Dept Members ({deptMatchingUnassigned.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setFilterMode('all')}
                      style={{
                        padding: '5px 12px', borderRadius: 6, border: '1px solid #64748b', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                        background: filterMode === 'all' ? '#64748b' : '#f8fafc',
                        color: filterMode === 'all' ? '#fff' : '#475569'
                      }}
                    >
                      🌐 All Force Unassigned ({unassignedPersonnel.length})
                    </button>
                  </div>
                ) : (
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>
                    Available Unassigned Personnel ({unassignedPersonnel.length})
                  </div>
                )}

                {/* Bulk Actions */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {filteredAddPool.length > 0 && (
                    <button
                      onClick={() => {
                        if (selectedAddIds.length === filteredAddPool.length) setSelectedAddIds([]);
                        else setSelectedAddIds(filteredAddPool.map(p => p.id));
                      }}
                      style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 12, fontWeight: 700, textDecoration: 'underline' }}
                    >
                      {selectedAddIds.length === filteredAddPool.length ? 'Deselect All' : `Select All Visible (${filteredAddPool.length})`}
                    </button>
                  )}

                  {selectedAddIds.length > 0 && (
                    <button
                      onClick={handleBulkAdd}
                      disabled={isSubmitting}
                      style={{ padding: '6px 14px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                    >
                      ➕ Assign Selected ({selectedAddIds.length})
                    </button>
                  )}
                </div>
              </div>

              {unassignedPersonnel.length === 0 ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#166534', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10 }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>🎉</div>
                  <div style={{ fontSize: 15, fontWeight: 800 }}>All Personnel Assigned!</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Every single personnel in this force is currently assigned to an active payroll group.</div>
                </div>
              ) : filteredAddPool.length === 0 ? (
                <div style={{ padding: 30, textAlign: 'center', color: '#94a3b8' }}>
                  No available personnel matching <strong>"{searchQuery}"</strong>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                    {paginatedAddPool.map((p) => {
                      const isSelected = selectedAddIds.includes(p.id);
                      const isDeptMatch = group.department_id && String(p.department_id) === String(group.department_id);

                      return (
                        <div
                          key={p.id}
                          style={{
                            padding: '12px 14px', border: `1px solid ${isSelected ? '#93c5fd' : isDeptMatch ? '#bae6fd' : '#e2e8f0'}`, borderRadius: 8,
                            background: isSelected ? '#eff6ff' : isDeptMatch ? '#f0f9ff' : '#fff',
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                if (isSelected) setSelectedAddIds(selectedAddIds.filter(id => id !== p.id));
                                else setSelectedAddIds([...selectedAddIds, p.id]);
                              }}
                            />
                            <div>
                              <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>{getPersonnelName(p)}</span>
                                {isDeptMatch && (
                                  <span style={{ padding: '1px 5px', borderRadius: 4, background: '#dcfce7', color: '#15803d', fontSize: 10, fontWeight: 800 }}>
                                    Dept Match
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>
                                ID: <strong style={{ color: '#0284c7' }}>{p.army_id || 'N/A'}</strong> | Rank: {p.rank || 'N/A'}
                              </div>
                            </div>
                          </div>

                          <button
                            onClick={() => handleQuickAdd(p.id)}
                            disabled={isSubmitting}
                            style={{ padding: '4px 10px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                          >
                            ➕ Add
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Add Personnel Tab Pagination Controls */}
                  <PaginationControls
                    currentPage={addPage}
                    totalItems={filteredAddPool.length}
                    pageSize={PAGE_SIZE}
                    onPageChange={setAddPage}
                  />
                </>
              )}
            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div style={{ padding: '14px 24px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 20px', background: '#475569', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
          >
            Close Window
          </button>
        </div>

      </div>
    </div>
  );
};

export default PayrollGroupsTab;
